import { useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import { AgreementProgressSection } from "../components/AgreementProgressSection";
import { ColorSelect } from "../components/ColorSelect";
import { Input } from "@/shared/ui/Input";
import { DatePicker } from "@/shared/ui/DatePicker";
import { Textarea } from "@/shared/ui/Textarea";
import { useColorOptions } from "../hooks";
import type { AgreementPreviewRow, AgreementRowInput } from "../types";

// Opens from the info button on a "ready" preview row, BEFORE commit (no loanId / GET / PATCH).
// Mirrors AgreementEditDrawer's look — a read-only money summary plus the editable detail fields —
// but writes its result into editRows[index].additional_data (which then rides to the backend at
// re-validate/commit) instead of PATCHing a created loan.
export function AgreementPreviewDetailDrawer({
  row,
  input,
  onSave,
  onClose,
}: {
  row: AgreementPreviewRow; // editable_fields + built additional_data + money summary source
  input: AgreementRowInput; // current edited core values (additional_data seed + money display)
  onSave: (additionalData: Record<string, string>) => void; // → editRows[index].additional_data + re-validate
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { data: colorOptions = [], isLoading: colorsLoading } = useColorOptions(true);

  // The model/technology_model display key is chosen in the name column (Feature C), so it is NOT
  // re-edited here — only the detail keys. The backend never puts the _id sibling into editable_fields.
  const isProductKey = (field: string) =>
    (row.type === "car" && field === "model") ||
    (row.type === "technology" && field === "technology_model");
  const detailFields = (row.editable_fields ?? []).filter((field) => !isProductKey(field));

  // Seed: a prior drawer save (input.additional_data) wins; otherwise the backend-built defaults on the
  // preview row (row.additional_data, e.g. color 'Oq'). Only the detail keys are tracked.
  const [form, setForm] = useState<Record<string, string>>(() => {
    const prior = input.additional_data ?? {};
    const built = (row.additional_data ?? {}) as Record<string, unknown>;
    const seed: Record<string, string> = {};
    detailFields.forEach((field) => {
      const value = field in prior ? prior[field] : built[field];
      seed[field] = value !== undefined && value !== null ? String(value) : "";
    });
    return seed;
  });

  const handleChange = useCallback(
    (field: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setForm((prev) => ({ ...prev, [field]: e.target.value }));
    },
    [],
  );

  const handleSave = useCallback(() => {
    onSave(form);
    onClose();
  }, [form, onSave, onClose]);

  function inputType(field: string): "date" | "number" | "text" {
    if (field === "production_date") return "date";
    if (field === "mileage") return "number";
    return "text";
  }

  // Read-only money summary, from the edited core values (strings). Blank → 0 for display only.
  const money = (value: string) => Number(value || 0).toLocaleString();

  // Portal to document.body so that fixed positioning is relative to the viewport, not
  // a transformed/overflow-clipped ancestor container (which would confine the backdrop).
  return createPortal(
    <div
      className="fixed inset-0 z-40 flex items-end justify-center bg-black/40 sm:items-center"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="relative z-50 flex max-h-[92dvh] w-full max-w-3xl flex-col rounded-t-card bg-white shadow-float sm:rounded-card lg:max-h-none">
        {/* Header */}
        <div className="border-b border-grey-100 px-4 py-4 sm:px-6">
          <div className="flex items-center justify-between">
            <h3 className="text-[16px] font-semibold text-grey-800">
              {t("import.agreements.previewDetail.drawerTitle")}
            </h3>
            <button
              type="button"
              onClick={onClose}
              className="text-grey-400 hover:text-grey-700"
              aria-label="close"
            >
              <X size={20} />
            </button>
          </div>
          <p className="mt-1 text-[12px] text-grey-500">
            {t("import.agreements.previewDetail.subtitle")}
          </p>
        </div>

        {/* Body — two-pane (B): left = read-only context, right = editable fields.
            On mobile the whole modal scrolls; on desktop each pane scrolls independently. */}
        <div className="flex-1 overflow-y-auto px-4 py-5 sm:px-6 lg:overflow-visible">
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)]">
            {/* LEFT — read-only context: payment progress + locked money summary */}
            <div className="lg:max-h-[70vh] lg:overflow-y-auto lg:pr-1">
              <p className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-grey-400">
                {t("import.agreements.detail.contextTitle")}
              </p>

              {/* Projected payment-progress (0087) — imported agreements already carry paid_amount, so
                  the backend projects the schedule + paid-history; it recomputes on every re-validate. */}
              {row.payments && row.payments.length > 0 && (
                <AgreementProgressSection
                  payments={row.payments}
                  paidAmount={row.paid_amount}
                  remainder={row.remainder ?? "0"}
                  startDate={input.first_payment_date || row.payments[0]?.payment_date}
                />
              )}

              {/* Locked money summary — context only, no inputs */}
              <div className="rounded-card bg-grey-50 px-4 py-3">
                <p className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-grey-400">
                  {t("import.agreements.edit.sectionLocked")}
                </p>
                <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-[13px]">
                  <div>
                    <span className="text-grey-500">{t("import.agreements.edit.lockedSellingPrice")}: </span>
                    <span className="font-medium text-grey-700">{money(input.selling_price)}</span>
                  </div>
                  <div>
                    <span className="text-grey-500">{t("import.agreements.edit.lockedDownPayment")}: </span>
                    <span className="font-medium text-grey-700">{money(input.down_payment)}</span>
                  </div>
                  <div>
                    <span className="text-grey-500">{t("import.agreements.edit.lockedMonthlyPayment")}: </span>
                    <span className="font-medium text-grey-700">{money(input.monthly_payment)}</span>
                  </div>
                  <div>
                    <span className="text-grey-500">{t("import.agreements.edit.lockedPaymentCount")}: </span>
                    <span className="font-medium text-grey-700">{input.payment_count || "—"}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* RIGHT — editable detail fields, driven by row.editable_fields (product key excluded) */}
            <div className="lg:max-h-[70vh] lg:overflow-y-auto lg:pl-1">
              <p className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-grey-400">
                {t("import.agreements.detail.editTitle")}
              </p>
              {detailFields.length === 0 ? (
                <p className="py-4 text-center text-[13px] text-grey-400">
                  {t("import.agreements.previewDetail.noFields")}
                </p>
              ) : (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  {detailFields.map((field) => {
                    const isMissing = !(form[field] ?? "").trim();
                    const isTextarea = field === "comment";
                    const labelKey = `import.agreements.edit.fields.${field}` as const;
                    return (
                      <div key={field} className={field === "comment" ? "sm:col-span-2" : ""}>
                        <label
                          htmlFor={`preview-detail-${field}`}
                          className="mb-1 flex items-center gap-1 text-[13px] font-medium text-grey-700"
                        >
                          {t(labelKey)}
                          {isMissing && (
                            <span className="ml-1 rounded-full bg-warning-bg px-1.5 py-0.5 text-[10px] font-semibold text-warning">
                              !
                            </span>
                          )}
                        </label>
                        {field === "color" ? (
                          <ColorSelect
                            id={`preview-detail-${field}`}
                            value={form.color ?? ""}
                            options={colorOptions}
                            isLoading={colorsLoading}
                            isMissing={isMissing}
                            placeholder={t("import.agreements.edit.colorPlaceholder")}
                            onChange={(name) => setForm((prev) => ({ ...prev, color: name }))}
                          />
                        ) : isTextarea ? (
                          <Textarea
                            id={`preview-detail-${field}`}
                            value={form[field] ?? ""}
                            onChange={handleChange(field)}
                            rows={3}
                            className={`resize-none${isMissing ? " border-warning-border" : ""}`}
                          />
                        ) : inputType(field) === "date" ? (
                          <DatePicker
                            id={`preview-detail-${field}`}
                            value={form[field] ?? ""}
                            onChange={(v) => setForm((prev) => ({ ...prev, [field]: v }))}
                            className={isMissing ? "border-warning-border" : undefined}
                          />
                        ) : (
                          <Input
                            id={`preview-detail-${field}`}
                            type={inputType(field)}
                            value={form[field] ?? ""}
                            onChange={handleChange(field)}
                            className={isMissing ? "border-warning-border" : undefined}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer actions */}
        <div className="flex justify-end gap-3 border-t border-grey-100 px-4 py-4 sm:px-6">
          <button
            type="button"
            onClick={onClose}
            className="rounded-control border border-grey-200 px-5 py-2.5 text-[14px] font-medium text-grey-700 hover:bg-grey-50"
          >
            {t("import.agreements.edit.cancelBtn")}
          </button>
          <button
            type="button"
            onClick={handleSave}
            className="rounded-control bg-green px-5 py-2.5 text-[14px] font-semibold text-white transition hover:bg-green-dark active:scale-95"
          >
            {t("import.agreements.edit.saveBtn")}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
